import React, { useCallback, useMemo } from 'react';
import {
  GRANT_CHECK_TYPE, GRANT_STATES, useCallSuccess, useGrants, useI18n, useRootContext,
} from '@jarvis/react-portal-addons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import SolutionCardTemplate from './SolutionCardTemplate';
import { useCardsVisibility } from './useCardsVisibility';
import useOpenStoreForHpApp from '../../../hooks/useOpenStoreForHpApp';
import Images from '../../../assets/images';
import { DEEPLINKS } from '../../../utils/constants';
import useDevicesProgramInfoApiCall from './useDevicesProgramInfo';
import { DASHBOARD_ENTITLEMENTS } from '../../../utils/printerStatus';
import { useInstantInkSubscriptionCounters } from './useInstantInkSubscriptionCounters';

const grantsStates = [GRANT_STATES.ENABLED, GRANT_STATES.PENDING];

const instantInkGrantsBase = [
  {
    grant: DASHBOARD_ENTITLEMENTS.instantInk,
    states: grantsStates,
  },
  {
    grant: DASHBOARD_ENTITLEMENTS.printPlans,
    states: grantsStates,
  },
];

const SolutionCards = ({ deviceCache }) => {
  const { shell: { grants: grantsInterface, orgSelector } } = useRootContext();
  const { t } = useI18n();

  const flags = useFlags();

  const {
    grantsSupport,
  } = flags;

  const {
    showInstantInkCard,
    showHpAllInPlanCard,
    showSustainabilityCard,
    showGetHpAppCard,
    instantInkCardLinkToOverview,
  } = useCardsVisibility({ deviceCache });

  const { grantsCall } = useGrants({
    grants: [],
    init: false,
    checkType: GRANT_CHECK_TYPE.DEVICES,
    grantsInterface,
  });

  const devicesProgramInfos = useDevicesProgramInfoApiCall();

  const { instantInkEligiblePrinterCounter, instantInkEnrolledPrinterCounter } = useInstantInkSubscriptionCounters({
    devicesProgramInfos: devicesProgramInfos.data,
    grantsCall: grantsCall.data,
  });

  const instantInkCardDependenciesReady = showInstantInkCard && !(grantsCall.pending || devicesProgramInfos.pending);

  const showInstantInkEnrolledCard = instantInkCardDependenciesReady && instantInkEnrolledPrinterCounter > 0;

  const showInstantInkEligibleCard = instantInkCardDependenciesReady && !showInstantInkEnrolledCard;

  const onDeviceCacheSuccess = useCallback(
    (_, devices) => {
      if (grantsSupport) {
        let grantsToCheck = [];

        devices.forEach(device => {
          grantsToCheck = [
            ...grantsToCheck,
            ...instantInkGrantsBase.map(grant => ({
              ...grant,
              deviceId: device.deviceId,
              tenantId: orgSelector.getOrgTenantId(),
            })),
          ];
        });

        grantsCall.makeApiCall({
          grants: grantsToCheck,
          states: grantsStates,
        });
      }

      devicesProgramInfos.makeApiCall({
        devices,
      });
    },
    [devicesProgramInfos, grantsCall, grantsSupport, orgSelector],
  );

  useCallSuccess({
    call: deviceCache,
    onSuccess: onDeviceCacheSuccess,
  });

  const { openStoreForHpApp } = useOpenStoreForHpApp();

  const CardList = useMemo(() => [
    showGetHpAppCard
    && {
      headerText: t('dashboard.getTheHpAppCard.header'),
      title: t('dashboard.getTheHpAppCard.title'),
      description: t('dashboard.getTheHpAppCard.description'),
      icon: Images.getTheHpAppIcon,
      onClick: openStoreForHpApp,
      footerText: t('dashboard.getTheHpAppCard.footer'),
      dataTestIds: {
        card: 'get-the-hp-app-card',
        header: 'get-the-app-card-header',
        title: 'get-the-app-card-title',
        description: 'get-the-app-card-description',
        link: 'get-the-app-card-link',
      },
    },
    showInstantInkEnrolledCard
    && {
      headerText: instantInkEnrolledPrinterCounter === 1 ? t('dashboard.instantInkEnrolledCard.headerSingular', {
        totalPrinters: deviceCache?.data?.length || 0,
      }) : t('dashboard.instantInkEnrolledCard.headerPlural', {
        totalEnrolled: instantInkEnrolledPrinterCounter,
        totalPrinters: deviceCache?.data?.length || 0,
      }),
      title: t('dashboard.instantInkEnrolledCard.title'),
      description: t('dashboard.instantInkEnrolledCard.description'),
      icon: Images.truckIcon,
      footerHyperlink: instantInkCardLinkToOverview ? DEEPLINKS.INSTANT_INK_OVERVIEW : DEEPLINKS.INSTANT_INK,
      footerText: t('dashboard.instantInkEnrolledCard.footer'),
      showCheckmarkCircleIcon: true,
      dataTestIds: {
        card: 'instant-ink-enrolled-card',
        header: 'instant-ink-enrolled-card-header',
        title: 'instant-ink-enrolled-card-title',
        description: 'instant-ink-enrolled-card-description',
        link: 'instant-ink-enrolled-card-link',
      },
    },
    showInstantInkEligibleCard
    && {
      headerText: instantInkEligiblePrinterCounter === 1 ? t('dashboard.instantInkEligibleCard.headerSingular', {
        totalEligible: instantInkEligiblePrinterCounter,
      }) : t('dashboard.instantInkEligibleCard.headerPlural', {
        totalEligible: instantInkEligiblePrinterCounter,
      }),
      title: t('dashboard.instantInkEnrolledCard.title'),
      description: t('dashboard.instantInkEligibleCard.description'),
      icon: Images.truckIcon,
      footerHyperlink: instantInkCardLinkToOverview ? DEEPLINKS.INSTANT_INK_OVERVIEW : DEEPLINKS.INSTANT_INK,
      footerText: t('dashboard.instantInkEligibleCard.footer'),
      dataTestIds: {
        card: 'instant-ink-eligible-card',
        header: 'instant-ink-eligible-card-header',
        title: 'instant-ink-eligible-card-title',
        description: 'instant-ink-eligible-card-description',
        link: 'instant-ink-eligible-card-link',
      },
    },
    showHpAllInPlanCard
    && {
      headerText: 'Printer, Ink and 24/7 Support',
      title: 'HP All-in Plan',
      description: `Enjoy hassle-free printing with the HP All-In Plan.
      Get all your printing essentials for one low monthly payment.`,
      icon: Images.printerDropIcon,
      footerHyperlink: '/',
      footerText: 'Learn more',
      dataTestIds: {
        card: 'hp-all-in-plan-card',
        header: 'hp-all-in-plan-card-header',
        title: 'hp-all-in-plan-card-title',
        description: 'hp-all-in-plan-card-description',
        link: 'hp-all-in-plan-card-link',
      },
    },
    showSustainabilityCard
    && {
      headerText: t('dashboard.sustainabilityCard.header'),
      title: t('dashboard.sustainabilityCard.title'),
      description: t('dashboard.sustainabilityCard.description'),
      icon: Images.sustainabilityPlanetIcon,
      footerHyperlink: '/forestfirst',
      footerText: t('dashboard.sustainabilityCard.footer'),
      dataTestIds: {
        card: 'sustainability-card',
        header: 'sustainability-card-header',
        title: 'sustainability-card-title',
        description: 'sustainability-card-description',
        link: 'sustainability-card-link',
      },
    },
  ].filter(Boolean),
  [
    showGetHpAppCard,
    t,
    openStoreForHpApp,
    showInstantInkEnrolledCard,
    instantInkEnrolledPrinterCounter,
    deviceCache?.data?.length,
    instantInkCardLinkToOverview,
    showInstantInkEligibleCard,
    instantInkEligiblePrinterCounter,
    showHpAllInPlanCard,
    showSustainabilityCard]);

  if (!CardList.length) {
    return null;
  }

  return CardList.map(cardInfo => (
    <SolutionCardTemplate
      key={cardInfo.dataTestIds.card} {...cardInfo}
      data-testid={cardInfo.dataTestIds.card}
    />
  ));
};

export default SolutionCards;
